"use client";

import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import PhoneNumberForm from "@/app/components/modals/verify/phoneNumberForm";
import CodeConfirmForm from "@/app/components/modals/verify/codeConfirmForm";
import dynamic from "next/dynamic";
import { CreditCard } from "@mui/icons-material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";

const StripeCardForm = dynamic(() => import("./StripeCardForm.jsx"), {
  loading: () => (
    <Box sx={{ textAlign: "center", padding: "20px" }}>
      <Typography variant="body1" color="textSecondary">
        Loading payment form...
      </Typography>
    </Box>
  ),
  ssr: false,
});

const VerifyIdentityModal = ({
  open,
  handleClose,
  currentMeetingData = null,
  contextMessage = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const profile = useSelector((state) => state.user.profile);

  const [activeTab, setActiveTab] = useState(0); // 0 = phone, 1 = card
  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreditCardLoading, setIsCreditCardLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setPhoneSent(false);
      setPhoneNumber("");
      setIsLoading(false);
      setIsCreditCardLoading(false);
      setActiveTab(0);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setErrorMessage("");
    setPhoneSent(false);
    setPhoneNumber("");
  };

  const handlePhoneSubmitted = (submittedPhoneNumber) => {
    setPhoneNumber(submittedPhoneNumber);
    setPhoneSent(true);
  };

  const getDescription = () => {
    if (activeTab === 0) {
      return phoneSent
        ? "Enter the 6-digit code sent to your phone to verify your number. The code will expire shortly."
        : "Enter your mobile number to get a one-time SMS code. We'll send a 6-digit code to verify your number.";
    }
    return "Enter your credit card to verify identity. You won’t be charged until you schedule a meeting.";
  };

  // Build the body content for the ConfirmDialog
  const bodyContent = (
    <Box>
      {contextMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {contextMessage}
        </Alert>
      )}
      {/* Header with tabs - Desktop: title left, tabs right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "center" : "space-between",
          alignItems: isMobile ? "stretch" : "center",
          mb: 3,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Final step! Select your verification method.
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: "auto",
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
            "& .MuiTab-root": {
              minHeight: 40,
              fontSize: "0.9rem",
              fontWeight: 500,
              textTransform: "none",
              minWidth: isMobile ? "50%" : 100,
              padding: isMobile ? "8px 16px" : "6px 12px",
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIphoneIcon fontSize="small" />
                Phone
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard fontSize="small" />
                Card
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Description text */}
      <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
        {getDescription()}
      </Typography>

      {/* Form Content */}
      <Box>
        {activeTab === 0 ? (
          phoneSent ? (
            <CodeConfirmForm
              phoneNumber={phoneNumber}
              handleClose={handleClose}
              setErrorMessage={setErrorMessage}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              currentMeetingData={currentMeetingData}
            />
          ) : (
            <PhoneNumberForm
              countryCode={profile?.country_code}
              onPhoneSubmitted={handlePhoneSubmitted}
              setErrorMessage={setErrorMessage}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          )
        ) : (
          <StripeCardForm
            handleClose={handleClose}
            setErrorMessage={setErrorMessage}
            setIsCreditCardLoading={setIsCreditCardLoading}
            isCreditCardLoading={isCreditCardLoading}
            currentMeetingData={currentMeetingData}
          />
        )}

        <Box sx={{ mt: 2 }}>
          {errorMessage ? (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {activeTab === 0
                ? phoneSent
                  ? "Enter the 6-digit code sent to your phone."
                  : "Used only for verification, never shared."
                : "All transactions are secure and encrypted."}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      title="Verify it's you"
      body={bodyContent}
      showCloseIcon={true}
      showConfirmButton={false}
      showIgnoreButton={false}
      disableBackdropClick={true}
      disableEscapeKeyDown={false}
      modalStyle={isMobile ? "toast" : "center"}
      backdropStyle="modern"
    />
  );
};

export default VerifyIdentityModal;
